import moment from 'moment';

// Wall-clock format shared by the bundle slots and the slot planner.
export const SERVER_FMT = 'DD-MM-YYYY hh:mm:ss A';

const BUFFER_MS = 30 * 60 * 1000; // re-fetch ~30 min before the bundle expires
const JITTER_MS = 30 * 60 * 1000; // spread re-fetches over 0–30 min so screens don't stampede
const MIN_DELAY_MS = 60 * 1000;

// Parse the server's UTC timestamp regardless of whether it carries a trailing 'Z'.
export const parseServerUtc = (value) => {
  if (!value) return NaN;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  return Date.parse(hasTz ? value : `${value}Z`);
};

// serverUtc - deviceNow at fetch time; added to Date.now() to correct a wrong device clock.
export const clockOffset = (serverTimeUtc, nowMs = Date.now()) => {
  const offset = parseServerUtc(serverTimeUtc) - nowMs;
  return Number.isFinite(offset) ? offset : 0;
};

// Current local wall-clock in the screen's time zone, derived from the server-anchored offset
// (works offline, DST-correct via Intl). Returns the local calendar day and a moment-parseable
// string in SERVER_FMT.
export const computeLocal = (offsetMs, timeZone, nowMs = Date.now()) => {
  const correctedUtc = new Date(nowMs + (offsetMs || 0));
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(correctedUtc)
      .map((p) => [p.type, p.value]),
  );

  const hour = parts.hour === '24' ? 0 : Number(parts.hour); // Intl may emit '24' at midnight
  const local = moment({
    year: Number(parts.year),
    month: Number(parts.month) - 1,
    day: Number(parts.day),
    hour,
    minute: Number(parts.minute),
    second: Number(parts.second),
  });

  return {
    dayKey: local.format('YYYY-MM-DD'),
    localStr: local.format(SERVER_FMT),
  };
};

// How far `timeZone` is ahead of UTC at the instant `utcMs` (DST-correct via Intl).
const zoneOffsetMs = (utcMs, timeZone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(utcMs))
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    parts.hour === '24' ? 0 : Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
};

/**
 * The instant (epoch ms) of local wall-clock `time` ("HH:mm:ss") on `dayKey` ("YYYY-MM-DD") in
 * `timeZone`. Every tile of a video wall anchors its slot on this, so they agree to the millisecond.
 */
export const zonedToEpoch = (dayKey, time, timeZone) => {
  const [year, month, day] = dayKey.split('-').map(Number);
  const [hour, minute, second = 0] = time.split(':').map(Number);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  // Correct by the zone's offset, then once more in case that crossed a DST change.
  const guess = asUtc - zoneOffsetMs(asUtc, timeZone);
  return asUtc - zoneOffsetMs(guess, timeZone);
};

// Milliseconds until just after local midnight (+1s to land safely in the next day).
export const msUntilLocalMidnight = (
  offsetMs,
  timeZone,
  nowMs = Date.now(),
) => {
  const { localStr } = computeLocal(offsetMs, timeZone, nowMs);
  const local = moment(localStr, SERVER_FMT);
  return Math.max(
    local.clone().endOf('day').valueOf() - local.valueOf() + 1000,
    1000,
  );
};

// Delay before re-fetching the bundle: shortly before it expires, with jitter.
export const horizonRefreshDelay = (
  expiresAtUtc,
  offsetMs,
  nowMs = Date.now(),
  random = Math.random,
) => {
  const expiresInMs = parseServerUtc(expiresAtUtc) - (nowMs + (offsetMs || 0));
  if (!Number.isFinite(expiresInMs)) return MIN_DELAY_MS;
  const jitterMs = Math.floor(random() * JITTER_MS);
  return Math.max(expiresInMs - BUFFER_MS + jitterMs, MIN_DELAY_MS);
};
